import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ApprovalStatusBadge, RequestBadge } from "components/ApprovalStatusBadge";
import {
  ChangeRequestRow,
  requestId,
} from "./SignageBrokerageChangeRequests";

const TYPE_LABELS: Record<string, string> = {
  office: "Offices",
  agent: "Agents",
  team_member: "Teammembers",
};

const TYPE_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  office: { bg: "#e0f0ff", color: "#276ada" },
  agent: { bg: "#e0cfff", color: "#6b21a8" },
  team_member: { bg: "#e0f7fa", color: "#00838f" },
};

function sentenceLabel(value: string): string {
  const raw = value.trim().toLowerCase();
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function textValue(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function formatDate(value: unknown): string {
  const raw = String(value || "").trim();
  if (!raw || raw.startsWith("0001-01-01")) return "";
  if (!/^\d{4}-\d{2}-\d{2}T/.test(raw)) return raw;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function actorName(value: unknown): string {
  const row = asRecord(value);
  return textValue(row?.name);
}

function loadStoredRequest(id: string): ChangeRequestRow | null {
  try {
    const raw = sessionStorage.getItem(`brokerage-change-request:${id}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function DisabledField({
  label,
  value,
  wide = false,
}: {
  label: string;
  value?: string;
  wide?: boolean;
}) {
  return (
    <div className={`${wide ? "col-12" : "col-lg-6"} mb-3`}>
      <label className="form-label">{label}</label>
      <input className="form-control" value={value || ""} readOnly disabled />
    </div>
  );
}

function DisabledTextarea({ label, value }: { label: string; value?: string }) {
  return (
    <div className="col-12 mb-3">
      <label className="form-label">{label}</label>
      <textarea className="form-control" rows={3} value={value || ""} readOnly disabled />
    </div>
  );
}

function CardSection({
  iconClass,
  iconBg,
  iconColor,
  title,
  children,
}: {
  iconClass: string;
  iconBg: string;
  iconColor: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card bg-white shadow-lg br-8 mb-4">
      <div className="card-body p-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="badge p-2 br-8" style={{ background: iconBg }}>
            <i className={`${iconClass} fs-19 fw-light`} style={{ color: iconColor }} />
          </span>
          <p className="fs-18 m-0">{title}</p>
        </div>
        <div className="row">{children}</div>
      </div>
    </section>
  );
}

function firstOfficeAddress(changes: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!changes) return null;
  const list = changes.officeAddress;
  if (Array.isArray(list) && list[0] && typeof list[0] === "object") {
    return list[0] as Record<string, unknown>;
  }
  return asRecord(changes.address);
}

const SignageBrokerageChangeRequestView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId: requestIdParam } = useParams<{ requestId: string }>();
  const stateRequest = (location.state as { request?: ChangeRequestRow } | null)?.request;
  const request = useMemo(() => {
    if (stateRequest && requestId(stateRequest)) return stateRequest;
    return requestIdParam ? loadStoredRequest(requestIdParam) : null;
  }, [stateRequest, requestIdParam]);

  const isVendorRequests = location.pathname.includes("/vendorRequests");
  const type = String(request?.resource_type || "").trim().toLowerCase();
  const typeLabel = TYPE_LABELS[type] || sentenceLabel(type) || "—";
  const typeColors = TYPE_BADGE_COLORS[type] || { bg: "#E2E3E5", color: "#383D41" };
  const changes = asRecord(request?.changes);
  const address = firstOfficeAddress(changes);
  const professional = asRecord(changes?.professional_details);
  const officeIds = Array.isArray(changes?.officeIDs)
    ? changes.officeIDs.map((id) => String(id || "").trim()).filter(Boolean).join(", ")
    : "";
  const firstName = textValue(changes?.firstName, changes?.first_name);
  const lastName = textValue(changes?.lastName, changes?.last_name);
  const userName =
    `${firstName} ${lastName}`.trim() ||
    (type === "agent" || type === "team_member" ? textValue(request?.resource_name) : "");
  const officeName = type === "office"
    ? textValue(changes?.officeName, request?.resource_name)
    : "";
  const email = textValue(request?.resource_email, changes?.email);
  const hasPersonChanges = Boolean(
    firstName || lastName || email || textValue(changes?.jobTitle, changes?.phone1, changes?.phone2) || address
  );
  const hasOfficeChanges = type === "office" && Boolean(officeName || textValue(changes?.officeType) || address);
  const hasChangeFields = hasPersonChanges || hasOfficeChanges || Boolean(officeIds) || Boolean(professional);

  const goBack = () => {
    const listPath = location.pathname.replace(/\/view\/[^/]+\/?$/, "");
    navigate(listPath);
  };

  if (!request) {
    return (
      <div className="content container">
        <div className="po-header mb-4 shadow-border-datatables">
          <div className="po-top d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="po-title mb-0">View request</h4>
            <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={goBack}>
              Back
            </button>
          </div>
        </div>
        <div className="card box-shadow-datatables shadow-border-2 p-4">Request not found.</div>
      </div>
    );
  }

  return (
    <div className="content position-relative">
      <div className="content container">
        <div className="po-header mb-4 shadow-border-datatables">
          <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
            <div>
              <h4 className="po-title mb-1">View request</h4>
              <div className="po-subtitle">All fields are read only</div>
            </div>
            <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={goBack}>
              Back
            </button>
          </div>
        </div>

        <div className="box-shadow br-8 col-lg-7 container mb-4 p-lg-4 br-gray">
          <CardSection
            iconClass="ri-file-info-line"
            iconBg="#dbeafe"
            iconColor="#155dfc"
            title="Request Information"
          >
            <div className="col-lg-6 mb-3">
              <label className="form-label">Type</label>
              <div className="pt-1">
                <RequestBadge label={typeLabel} bg={typeColors.bg} color={typeColors.color} />
              </div>
            </div>
            <div className="col-lg-6 mb-3">
              <label className="form-label">Status</label>
              <div className="pt-1">
                <ApprovalStatusBadge status={request.status} />
              </div>
            </div>
            <DisabledField label="Operation" value={sentenceLabel(String(request.operation || ""))} />
            <DisabledField label="Resource Email" value={email} />
            <DisabledField label="User Name" value={userName} />
            <DisabledField label="Office Name" value={officeName} />
            {isVendorRequests ? (
              <DisabledField label="Vendor Name" value={textValue(request.vendor_name)} />
            ) : null}
            <DisabledTextarea label="Decision Note" value={textValue(request.decision_note)} />
            <DisabledField label="Cancelled By" value={actorName(request.cancelled_by)} />
            <DisabledField label="Reviewed By" value={actorName(request.reviewed_by)} />
            <DisabledField label="Reviewed At" value={formatDate(request.reviewed_at)} />
            <DisabledField label="Cancelled At" value={formatDate(request.cancelled_at)} />
          </CardSection>

          <CardSection
            iconClass={type === "office" ? "ri-building-4-line" : "ri-user-3-line"}
            iconBg={type === "office" ? "#f3e8ff" : "#dbeafe"}
            iconColor={type === "office" ? "#9810fa" : "#155dfc"}
            title={type === "office" ? "Office Changes" : "Personal Information"}
          >
            {!hasChangeFields ? (
              <div className="col-12 text-muted">No field changes in this request.</div>
            ) : null}
            {type === "office" ? (
              <>
                <DisabledField label="Office Name" value={textValue(changes?.officeName)} />
                <DisabledField label="Office Type" value={textValue(changes?.officeType)} />
              </>
            ) : (
              <>
                <DisabledField label="First Name" value={firstName} />
                <DisabledField label="Last Name" value={lastName} />
                <DisabledField label="Email" value={email} />
                <DisabledField label="Job Title" value={textValue(changes?.jobTitle)} />
                <DisabledField label="Phone" value={textValue(changes?.phone1)} />
                <DisabledField label="Secondary Phone" value={textValue(changes?.phone2)} />
                <DisabledField label="Offices" value={officeIds} wide />
              </>
            )}
            {address ? (
              <>
                <DisabledField label="Address Line 1" value={textValue(address.addressLine1)} wide />
                <DisabledField label="Address Line 2" value={textValue(address.addressLine2)} wide />
                <DisabledField label="City" value={textValue(address.city)} />
                <DisabledField label="State" value={textValue(address.state)} />
                <DisabledField label="Zip" value={textValue(address.zip)} />
                <DisabledField label="Country" value={textValue(address.country)} />
              </>
            ) : null}
            {professional ? (
              <>
                <DisabledField label="Agent ID" value={textValue(professional.agent_id)} />
                <DisabledField label="License Number" value={textValue(professional.license_number)} />
                <DisabledField label="License Expiry Date" value={textValue(professional.license_expiry_date)} />
              </>
            ) : null}
          </CardSection>

          <div className="d-flex gap-3 justify-content-end mb-4 mb-lg-0 mt-2">
            <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={goBack}>
              Back
            </button>
          </div>
        </div>
      </div>
      <style>{`
        input[type="text"], input.form-control, textarea.form-control {
          width: 100%;
          border-radius: 10px;
          padding: 10px 12px;
          border-width: 2px;
          border-style: solid;
          border-color: rgb(201, 206, 214);
          background-color: #f2f2f2a8 !important;
          outline: none;
          color: rgb(17, 24, 39);
        }
      `}</style>
    </div>
  );
};

export default SignageBrokerageChangeRequestView;
